import { HTMLAttributes, useState } from 'react'

import Container from '@/components/base/Container'
import Grid from '@/components/base/Grid'
import Motion from '@/components/base/Motion'
import Section from '@/components/base/Section'
import Typography from '@/components/base/Typography'

import { accordionTabRevealAnimation } from './animations'
import { cn } from '@/lib/utils'

export type AccordionTabProps = HTMLAttributes<HTMLDivElement> & {
  tabs: {
    title: string
    items: {
      title: string
      text: string
    }[]
  }[]
}

const AccordionItem = ({
  title,
  text,
  isOpen
}: {
  title: string
  text: string
  isOpen: boolean
}) => {
  const [open, setIsOpen] = useState(isOpen)

  return (
    <div className={cn('rounded-[8px] bg-gradient-to-b from-[#545454] to-[#303030] p-[1px]')}>
      <div className="flex flex-col rounded-[8px] bg-[#1D1D1D]">
        <button
          className="flex w-full cursor-pointer items-center justify-between px-spacing-sm py-spacing-lg [&_*]:hover:text-text-primary-dark"
          onClick={() => setIsOpen(!open)}
        >
          <Typography
            variant="large"
            className={cn(
              'transition-colors duration-200',
              open ? 'text-text-primary-dark' : 'text-text-secondary-invert-dark'
            )}
          >
            {title}
          </Typography>
        </button>
        <div
          className={cn([
            'grid overflow-hidden',
            'transition-all duration-300 ease-in-out-cubic',
            {
              'grid-rows-[0fr] opacity-0': !open,
              'grid-rows-[1fr] opacity-100': open
            }
          ])}
        >
          <div className="overflow-hidden">
            <div className="px-spacing-sm pb-spacing-lg">
              <Typography variant="regular" className="text-text-accent-light-dark">
                {text}
              </Typography>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const AccordionTab = ({ tabs, ...props }: AccordionTabProps) => {
  const [activeTab, setActiveTab] = useState(0)

  const handleTabClick = (index: number) => {
    setActiveTab(index)
  }

  return (
    <Motion animation={accordionTabRevealAnimation}>
      <Section {...props}>
        <Container>
          <div className={cn('rounded-[8px] bg-gradient-to-b from-[#545454] to-[#303030] p-[1px]')}>
            <div className="flex justify-around rounded-[8px] bg-[#1D1D1D] p-[7px]">
              <div
                className={cn(
                  'relative flex w-full',
                  'md:hidden',
                  'before:pointer-events-none before:absolute before:right-spacing-xs before:top-[calc(50%-3px)] before:h-[8px] before:w-[2px] before:rotate-45 before:rounded before:bg-white-dark',
                  'after:pointer-events-none after:absolute after:right-spacing-xs after:top-[calc(50%-3px)] after:h-[8px] after:w-[2px] after:translate-x-[-4px] after:-rotate-45 after:rounded after:bg-white-dark'
                )}
              >
                <select
                  className={cn(
                    'flex w-full appearance-none rounded-[4px] bg-[#282828] !bg-none p-spacing-xs text-text-primary-dark',
                    'font-roboto text-large font-medium text-text-accent-dark'
                  )}
                  value={activeTab}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                    handleTabClick(Number(e.target.value))
                  }}
                >
                  {tabs.map((tab, index) => (
                    <option key={`tab-${index}`} value={index}>
                      {tab.title}
                    </option>
                  ))}
                </select>
              </div>
              {tabs.map((tab, index) => (
                <button
                  key={`tab-${index}`}
                  className={cn(
                    'hidden flex-1 flex-col rounded-[4px] p-spacing-xs text-center transition-colors duration-200 md:flex',
                    '[&_*]:hover:text-text-accent-light',
                    {
                      'bg-[#282828]': activeTab === index,
                      'bg-transparent': activeTab !== index
                    }
                  )}
                  data-motion-accordion-tab
                >
                  <Typography
                    variant="large"
                    className={cn('duration-302 cursor-pointer transition-colors', {
                      'font-medium text-text-accent-light': activeTab === index,
                      'text-text-primary-dark': activeTab !== index
                    })}
                    onClick={() => handleTabClick(index)}
                  >
                    {tab.title}
                  </Typography>
                </button>
              ))}
            </div>
          </div>

          {tabs.map((tab, index) => (
            <div
              key={`tab-${index}`}
              className={cn('mt-spacing-sm', {
                hidden: activeTab !== index,
                block: activeTab === index
              })}
            >
              <Grid className="gap-spacing-sm">
                <div
                  className="col-span-4 flex flex-col gap-spacing-sm md:col-span-6 lg:col-span-4"
                  key={`col1-${index}`}
                >
                  {tab.items.map(
                    (item, itemIndex) =>
                      itemIndex % 3 === 0 && (
                        <AccordionItem
                          isOpen={itemIndex === 0}
                          key={`col1-item-${itemIndex}`}
                          title={item.title}
                          text={item.text}
                        />
                      )
                  )}
                </div>
                <div
                  className="col-span-4 flex flex-col gap-spacing-sm md:col-span-6 lg:col-span-4"
                  key={`col2-${index}`}
                >
                  {tab.items.map(
                    (item, itemIndex) =>
                      itemIndex % 3 === 1 && (
                        <AccordionItem
                          isOpen={itemIndex === 0}
                          key={`col2-item-${itemIndex}`}
                          title={item.title}
                          text={item.text}
                        />
                      )
                  )}
                </div>
                <div
                  className="col-span-4 flex flex-col gap-spacing-sm md:col-span-6 lg:col-span-4"
                  key={`col3-${index}`}
                >
                  {tab.items.map(
                    (item, itemIndex) =>
                      itemIndex % 3 === 2 && (
                        <AccordionItem
                          isOpen={itemIndex === 0}
                          key={`col3-item-${itemIndex}`}
                          title={item.title}
                          text={item.text}
                        />
                      )
                  )}
                </div>
              </Grid>
            </div>
          ))}
        </Container>
      </Section>
    </Motion>
  )
}

export default AccordionTab
